import React, { type FC } from 'react'
import { Box, type SxProps, type Theme, Typography, type TypographyProps } from '@mui/material'

import { boldFormatter, FormattedTranslation, type FormatTranslationProps } from '../Internationalization'
import type { BaseCommonProps } from './Base'

const baseValues = { b: boldFormatter, br: <br /> }

const truncatedTextStyle: SxProps<Theme> = { textOverflow: 'ellipsis', overflowX: 'hidden', whiteSpace: 'nowrap' }

type Props = FormatTranslationProps & Pick<BaseCommonProps, 'sx'> & Pick<TypographyProps, 'component' | 'align'>

const Base: FC<Props & Pick<TypographyProps, 'variant'>> = ({ id, values, ...props }) => (
    <Typography {...props}>
        <FormattedTranslation id={id} values={{ ...values, ...baseValues }} />
    </Typography>
)

export const Title: FC<Props> = (props) => <Base variant="subtitle1" {...props} />
export const SmallParagraph: FC<Props> = (props) => <Base variant="body2" {...props} />
export const MediumParagraph: FC<Props> = (props) => <Base variant="body1" {...props} />
export const Caption: FC<Props> = (props) => <Base variant="caption" {...props} />

const outputTextLeftToRight = (output: string): string => {
    return `\u2066${output}\u2069`
}

/**
 * If the text is too long and does not fit, it shows the end of the text and truncates the beginning
 * e.g.  "Lorem ipsum dolor sit amet." => "...sit amet."
 *
 * The &lrm; character is here to protect the first special character in the string from moving to the end in the rtl mode
 * e.g. in rtl for the string "$SYS/clients", the default behavior is "...S/clients$", which should be corrected to "...S/clients"
 * \u2066 (= &lrm; treated as a isolated whole span) is here from ReversedTruncatedText component, \u2069 is the closing of \u2066
 *
 */
export const ReversedTruncatedText: FC<BaseCommonProps & Readonly<{ children: string }>> = ({ children, sx, ...props }) => (
    <Box component="span" dir="rtl" sx={{ ...truncatedTextStyle, ...sx }} {...props}>
        {outputTextLeftToRight(children)}
    </Box>
)

export const Hyphen: FC = () => <Typography>-</Typography>
